import React, { useMemo } from "react"
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"
import { MdStraighten } from "react-icons/md"

import { useModalProvider } from "../../../Providers/ModalProvider"
import { EEG_WINDOW_TIME_PRESETS, EEG_WINDOW_LENGTH_PER_SEC_PRESETS, TimePreset } from "../Viewport/Components/XAxis"
import { editedLayoutGroupAtom, selectedLayoutAtom, selectedLayoutGroupAtom, uneditedLayoutGroupAtom } from "../../../Pages/Data/Visualize/DataReview/Atoms/Layout"
import { ConfigureEEGModal } from "../../../Pages/Data/Visualize/DataReview/Components/Modals/ConfigureEEGModal"
import { eegMontageConfigAtom } from "../../../Pages/Data/Visualize/DataReview/Atoms/EEGMontage"
import { EEG_SENSITIVITY, montageHasECGChannel } from "../../../Pages/Data/Visualize/DataReview/Types/EEG"
import { MobergDropdown, MobergDropdownSize } from "../../../Components/MobergDropdown/MobergDropdown"
import { MobergButton, MobergButtonShape } from "../../../Components/MobergButton/MobergButton"
import { MobergTheme } from "../../../Components/MobergThemes/MobergColors"
import { MobergIconSize } from "../../../Components/MobergIcon/MobergIcon"
import { VisualizationToolbar } from "../../../Pages/Data/Visualize/DataReview/Components/React/VisualizationToolbar"
import { EEGCollapseMenu } from "./EEGCollapseMenuSettings"
import { DataReviewEEGMontageDropdown } from "../../../Components/EEGMontageDropdownMenu/DataReviewEEGMontageDropdown"
import { useEEGCalibration, useMaintainMmPerSecond } from "../../../Pages/Data/Visualize/DataReview/Calibration/hooks"
import { EEGCalibrationRecord, EEGCalibrationStatus } from "../../../Pages/Data/Visualize/DataReview/Calibration/types"
import { EEGCalibrationModal } from "../ToolBar/Modals/EEGCalibrationModal"
import { formatDate } from "../../../Pages/Data/Visualize/DataReview/utils"
import { buildModalityTargetsForEEG  } from "../../../Pages/Data/Visualize/DataReview/Utils/modalityJumpTargets"
import { useModalityJumpTargets } from "../../../Pages/Data/Visualize/DataReview/Hooks/useModalityJumpTargets"

type EEGWindowSettingsProps = {
	visualizationArea: string
}

const getCalibrationTooltip = (status: EEGCalibrationStatus, record: EEGCalibrationRecord | undefined): string => {
	switch (status) {
		case EEGCalibrationStatus.VALID:
			return record?.savedAt
				? `Display calibrated - saved ${formatDate(new Date(record.savedAt))}`
				: "Display is calibrated"
		case EEGCalibrationStatus.OUTDATED:
			return record?.savedAt
				? `Display calibration is outdated - last saved ${formatDate(new Date(record.savedAt))}`
				: "Display calibration is outdated. Click to calibrate display."
		default:
			return "Calibrate EEG display for accurate mm and uV scaling"
	}
}

export function EEGWindowSettings(props: EEGWindowSettingsProps) {
	const { createModal } = useModalProvider()
	const setUneditedLayoutGroup = useSetRecoilState(uneditedLayoutGroupAtom)
	const setEditedLayoutGroup = useSetRecoilState(editedLayoutGroupAtom)
	const selectedLayoutGroup = useRecoilValue(selectedLayoutGroupAtom)
	const selectedLayout = useRecoilValue(selectedLayoutAtom)
	
	const layoutWindowId = {layoutId: selectedLayout?.id as string, windowId: props.visualizationArea}
	const atom = eegMontageConfigAtom(layoutWindowId)
	const [eegConfig, setEEGConfig] = useRecoilState(atom)
	const [viewStart, viewEnd] = eegConfig.viewScale.domain()
	const viewDuration = viewEnd.getTime() - viewStart.getTime()
	const [fileStart, fileEnd] = eegConfig.fileScale.domain()

	const { status: calibrationStatus, record: calibrationRecord, pxPerMm } = useEEGCalibration()
	const [rangeStart = 0, rangeEnd = 0] = eegConfig.viewScale.range()
	const chartWidthPx = Math.max(rangeEnd - rangeStart, 0)
	
	useMaintainMmPerSecond(
		eegConfig.targetMmPerSecond,
		calibrationStatus,
		pxPerMm,
		chartWidthPx,
		eegConfig.viewScale,
		eegConfig.fileScale,
		setEEGConfig
	)

	const calibratedTimePresets: TimePreset[] = useMemo(() => {
		if (calibrationStatus !== EEGCalibrationStatus.VALID || chartWidthPx <= 0 || pxPerMm <= 0) {
			return EEG_WINDOW_TIME_PRESETS
		}

		const derivedPresets = EEG_WINDOW_LENGTH_PER_SEC_PRESETS
			.map(preset => {
				const durationMs = Math.round((chartWidthPx / pxPerMm / preset.mmPerSecond) * 1000)

				return {
					label: `${preset.label} (${(durationMs / 1000).toFixed(1)} Sec)`,
					time: durationMs,
					targetMmPerSecond: preset.mmPerSecond,
				}
			})
			.filter(preset => preset.time > 0)

		return [...derivedPresets, ...EEG_WINDOW_TIME_PRESETS]
	}, [calibrationStatus, chartWidthPx, pxPerMm])

	const mmPerSecAnnotation =
		calibrationStatus === EEGCalibrationStatus.VALID &&
		chartWidthPx > 0 && viewDuration > 0 && pxPerMm > 0
			? `${(chartWidthPx / pxPerMm / (viewDuration / 1000)).toFixed(1)} mm/s`
			: "? mm/s"

	const calibrationButton = (
		<MobergButton
			shape={MobergButtonShape.SQUARE}
			theme={calibrationStatus === EEGCalibrationStatus.VALID ? MobergTheme.BLUE : MobergTheme.RED}
			active={true}
			onClick={() => createModal(<EEGCalibrationModal />)}
			tooltip={getCalibrationTooltip(calibrationStatus, calibrationRecord)}
		>
			<MdStraighten size={MobergIconSize.SMALL} />
		</MobergButton>
	)

	function renderConfigureModal() {
		if (!selectedLayout) {
			console.error("Couldn't render the modal because a layout was not selected")
			return
		}

		setUneditedLayoutGroup(selectedLayoutGroup)
		setEditedLayoutGroup(selectedLayoutGroup)

		createModal(
			<ConfigureEEGModal
				windowId={props.visualizationArea}
				layoutId={selectedLayout.id}
			/>
		)
	}


	const visualizationSpecificSettings = (<>
		<MobergDropdown 
			label={"Sensitivity"}
			selectedValue={eegConfig.sensitivityMicroVolts}
			size={MobergDropdownSize.SMALL} 
			onChange={value => setEEGConfig(previous => ({ ...previous, sensitivityMicroVolts: parseFloat(value as string) }))}
			options={Object.entries(EEG_SENSITIVITY).map(([label, value]) => ({ label, value }))}
		/>

		<DataReviewEEGMontageDropdown 
			label={"Montage"}
			currentMontage={eegConfig.montage} 
			updateCurrentMontage={montage => setEEGConfig(previous => ({ ...previous, montage }))}
		/>

		<EEGCollapseMenu
			eegConfig={eegConfig}
			setEEGConfig={setEEGConfig}
			showECGSensitivity={montageHasECGChannel(eegConfig.montage)}
		/>
	</>)

	const modalityTargets = useModalityJumpTargets(fileStart, fileEnd, buildModalityTargetsForEEG)

	return (
		<VisualizationToolbar 
			windowId={props.visualizationArea}
			layoutId={selectedLayout?.id as string}
			atomKey={atom.key}
			viewDuration={viewDuration}
			visualizationSpecificSettings={visualizationSpecificSettings}
			timePresetOptions={calibratedTimePresets}
			atomValue={eegConfig}
			setAtom={setEEGConfig}
			renderConfigureModal={renderConfigureModal}
			actionButtons={calibrationButton}
			viewDurationAnnotation={mmPerSecAnnotation}
			modalityTargets={modalityTargets}
		/>
	)
}
